/**
 * Command-palette search wiring (BUG-096 tracks + BUG-098 cross-entity).
 *
 * Pure helpers that build the list queries the ⌘K palette fires for each of
 * its server-backed groups (tracks / presets / accounts / selectors / promos)
 * plus the deep links the «show all» rows navigate to. Kept side-effect-free
 * so the wiring is unit-testable without a React/DOM harness (mirrors
 * `buildModerationQueryParams` in `api/tracks.ts`).
 */

import type { TracksQueryParams } from '../../api/tracks';
import type { PresetsQueryParams } from '../../api/presets';
import type { AccountsQueryParams } from '../../api/accounts';
import type { SelectorsListQueryParams } from '../../api/selectors';
import type { PromoCodesListQueryParams } from '../../api/promo-codes';

/** Top-N tracks surfaced inline in the palette before «show all». */
export const PALETTE_TRACK_RESULTS = 5;

/** Top-N rows surfaced per cross-entity group before «show all» (BUG-098). */
export const PALETTE_ENTITY_RESULTS = 5;

/**
 * Build the lightweight tracks query for the palette. `library` is left unset
 * so BOTH libraries are searched (the endpoint allows omitting it — see
 * `TracksQueryParams.library`). Sorting is left empty so the endpoint applies
 * its default `created_at desc`.
 */
export function buildTrackSearchParams(query: string): TracksQueryParams {
  return {
    pagination: { pageIndex: 0, pageSize: PALETTE_TRACK_RESULTS },
    sorting: [],
    filters: { q: query.trim() },
  };
}

/**
 * Deep link to the catalog with the search prefilled. The catalog reads `q`
 * from the URL via `useTrackFilters`, so navigating here applies the search.
 */
export function buildCatalogSearchPath(query: string): string {
  return buildSectionSearchPath('/catalog', query);
}

// ---------------------------------------------------------------------------
// BUG-098 — cross-entity list queries. Each entity's list endpoint accepts a
// `q` ILIKE filter server-side (confirmed in the BUG report); we cap results
// at PALETTE_ENTITY_RESULTS so the palette stays light. The hooks themselves
// (api/presets · api/accounts · api/selectors · api/promo-codes) build the
// request URL — these helpers only assemble the params object.
// ---------------------------------------------------------------------------

export function buildPresetSearchParams(query: string): PresetsQueryParams {
  return {
    filters: { q: query.trim() },
    limit: PALETTE_ENTITY_RESULTS,
  };
}

export function buildAccountSearchParams(query: string): AccountsQueryParams {
  return {
    filters: { q: query.trim() },
    limit: PALETTE_ENTITY_RESULTS,
  };
}

export function buildSelectorSearchParams(
  query: string,
): SelectorsListQueryParams {
  return {
    filters: { q: query.trim() },
    limit: PALETTE_ENTITY_RESULTS,
  };
}

export function buildPromoSearchParams(
  query: string,
): PromoCodesListQueryParams {
  return {
    filters: { q: query.trim() },
    limit: PALETTE_ENTITY_RESULTS,
  };
}

/**
 * Deep link to a section's list page with the search prefilled. Every target
 * list page (`/presets`, `/accounts`, `/selectors`, `/promos`, `/catalog`)
 * reads `q` from the URL, so the search carries over on «show all».
 */
export function buildSectionSearchPath(section: string, query: string): string {
  const sp = new URLSearchParams({ q: query.trim() });
  return `${section}?${sp.toString()}`;
}
